import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../shared/SafeIcon';
import AdviceSection from './AdviceSection';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function ResultsView({ data, onRestart }) {
  const recapRef = useRef(null);
  
  const {
    careName = 'your loved one',
    targetArea = '',
    relationship = '',
    timeline = '',
    situation = '',
    challenges = [],
    nightCare = '',
    cognitive = '',
    symptoms = [],
    medical = '',
    financial = ''
  } = data;

  const encodedArea = encodeURIComponent(targetArea.trim() || 'UK');
  const careHomeSearchUrl = `https://memovo.co.uk/care-search?q=${encodedArea}&search=${encodedArea}&from=home`;

  const buildContactMessage = () => {
    const message = `Hi Memovo Concierge,

I need some guidance. Here is the profile I built for ${careName}:

• Target Area: ${targetArea}
• Care is for: ${relationship}
• Timeline: ${timeline}
• Current Situation: ${situation}
• Daily Challenge: ${challenges.join(', ')}${nightCare !== 'No support needed at night' ? `. Night needs: ${nightCare}` : ''}
• Cognitive Health: ${cognitive}${symptoms.length > 0 ? `. Symptoms: ${symptoms.join(', ')}` : ''}
• Medical Needs: ${medical}
• Financial Bracket: ${financial}

Please contact me to discuss suitable options.`;

    return `https://memovo.co.uk/contact?finder=${encodeURIComponent(message)}`;
  };

  const handleDownloadPDF = async () => {
    const element = recapRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Memovo-Care-Profile-${careName}.pdf`);
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div className="max-w-5xl mx-auto">
        
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <SafeIcon icon={FiIcons.FiCheck} className="text-4xl" />
          </motion.div>
          <h1 className="text-4xl font-extrabold text-[#4C1D95] mb-4">Your Care & Funding Guide</h1>
          <p className="text-xl text-slate-600">
            Profile completed for <span className="font-semibold text-[#7C3AED]">{careName}</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="lg:col-span-2 space-y-8">
            <AdviceSection data={data} />

            <div ref={recapRef} className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 text-[#7C3AED] rounded-xl">
                    <SafeIcon icon={FiIcons.FiFileText} className="text-xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Care Profile Recap</h2>
                </div>
                <button 
                  onClick={handleDownloadPDF}
                  className="hidden sm:flex items-center gap-2 text-sm text-purple-600 font-bold hover:text-purple-800"
                >
                  <SafeIcon icon={FiIcons.FiDownload} />
                  Download PDF
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {[
                  { label: "Target Area", value: targetArea },
                  { label: "Relationship", value: relationship },
                  { label: "Timeline", value: timeline },
                  { label: "Living Situation", value: situation },
                  { label: "Nursing Needs", value: medical },
                  { label: "Financial Bracket", value: financial },
                  { label: "Cognitive Status", value: cognitive },
                  { label: "Night Support", value: nightCare },
                ].map((item, i) => (
                  <div key={i} className="border-b border-slate-50 pb-4">
                    <dt className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{item.label}</dt>
                    <dd className="text-slate-900 font-medium">{item.value}</dd>
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <dt className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Key Challenges</dt>
                  <dd className="text-slate-900 font-medium">{challenges.join(', ')}</dd>
                </div>
                {symptoms.length > 0 && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Reported Symptoms</dt>
                    <dd className="text-slate-900 font-medium">{symptoms.join(', ')}</dd>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-[#4C1D95] rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
              <SafeIcon icon={FiIcons.FiMessageCircle} className="text-4xl text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold mb-3">Memovo Concierge</h3>
              <p className="text-purple-100 text-sm mb-8 leading-relaxed">
                Send this profile to our specialist team. We will manually review your details and find the most suitable providers for {careName}.
              </p>
              <a 
                href={buildContactMessage()}
                className="block w-full text-center bg-teal-500 text-white font-bold py-4 px-4 rounded-xl hover:bg-teal-400 transition-all shadow-lg active:scale-95"
              >
                Send to Concierge
              </a>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiSearch} className="text-purple-600" />
                Find Providers
              </h3>
              <div className="space-y-3">
                <a href={careHomeSearchUrl} className="flex items-center justify-between w-full p-4 rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50 transition-all group">
                  <div>
                    <span className="block font-bold text-slate-900 group-hover:text-[#7C3AED]">Search Care Homes</span>
                    <span className="text-xs text-slate-500">Available in {targetArea}</span>
                  </div>
                  <SafeIcon icon={FiIcons.FiChevronRight} className="text-slate-400 group-hover:text-[#7C3AED]" />
                </a>
              </div>
            </div>

            <button onClick={onRestart} className="w-full text-slate-400 hover:text-slate-600 text-xs font-bold uppercase tracking-widest py-4">
              Restart Assessment
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}